import React, { useState, useEffect } from 'react';
import { useParams, useNavigate } from 'react-router-dom';
import { useAuth } from '../../contexts/AuthContext';
import { supabase } from '../../lib/supabase';
import { useTranslation } from 'react-i18next';
import { Trophy, Users, ArrowLeft, Save, Share2, Check, Copy, X, Eye, Settings, Crown, Image as ImageIcon, Upload, Zap, BarChart3 } from 'lucide-react';
import { Button, Input, DarkModeToggleCompact, Modal } from '../../components/ui';

export const WkLeaguePage = () => {
    const { id } = useParams();
    const { user } = useAuth();
    const navigate = useNavigate();
    const { t } = useTranslation();

    const [league, setLeague] = useState(null);
    const [matches, setMatches] = useState([]);
    const [predictions, setPredictions] = useState({});
    const [leaderboard, setLeaderboard] = useState([]);
    const [loading, setLoading] = useState(true);
    const [activeTab, setActiveTab] = useState('predictions'); // predictions, leaderboard, rules, settings
    const [saving, setSaving] = useState(false);
    const [copied, setCopied] = useState(false);
    const [hasUnsavedChanges, setHasUnsavedChanges] = useState(false);
    const [upgradeLoading, setUpgradeLoading] = useState(false);
    const [uploadingLogo, setUploadingLogo] = useState(false);
    
    // Bonus Questions State
    const [bonusAnswers, setBonusAnswers] = useState({ winner: '', topscorer: '' });
    const [competitorBonus, setCompetitorBonus] = useState(null);

    // Competitor Inspection State
    const [selectedCompetitor, setSelectedCompetitor] = useState(null);
    const [competitorPredictions, setCompetitorPredictions] = useState({});
    const [compLoading, setCompLoading] = useState(false);

    useEffect(() => {
        if (user && id) {
            fetchLeagueData();
        }
    }, [user, id]);

    const fetchCompetitorPredictions = async (competitor) => {
        setSelectedCompetitor(competitor);
        setCompLoading(true);
        try {
            const { data, error } = await supabase
                .from('wk_predictions')
                .select('*')
                .eq('user_id', competitor.user_id);
            
            if (error) throw error;
            
            const predMap = {};
            data.forEach(p => {
                predMap[p.match_id] = {
                    home: p.predicted_home_score,
                    away: p.predicted_away_score,
                    points: p.points_awarded,
                    isJoker: p.is_joker
                };
            });
            setCompetitorPredictions(predMap);

            // Fetch bonus answers for competitor
            const { data: bData } = await supabase
                .from('wk_bonus_answers')
                .select('*')
                .eq('user_id', competitor.user_id)
                .eq('league_id', id)
                .maybeSingle();
            setCompetitorBonus(bData);
        } catch (err) {
            console.error(err);
        } finally {
            setCompLoading(false);
        }
    };

    const handleShare = () => {
        if (!league) return;
        const shareUrl = `${window.location.origin}/wkpoule/join/${league.invite_code}`;
        navigator.clipboard.writeText(shareUrl);
        setCopied(true);
        setTimeout(() => setCopied(false), 2000);
    };

    const handleWhatsAppShare = () => {
        if (!league) return;
        const shareUrl = `${window.location.origin}/wkpoule/join/${league.invite_code}`;
        const message = `Doe mee met de WK 2026 Poule van *${league.name}* op ProPoule! 🏆⚽\n\nVul direct je voorspellingen in via deze link:\n${shareUrl}\n\nSucces! 🍀`;
        const whatsappUrl = `https://wa.me/?text=${encodeURIComponent(message)}`;
        window.open(whatsappUrl, '_blank');
    };
    
    const fetchLeagueData = async () => {
        try {
            setLoading(true);
            
            // 1. Fetch League details
            const { data: leagueData, error: leagueError } = await supabase
                .from('wk_leagues')
                .select('*')
                .eq('id', id)
                .single();
            if (leagueError) throw leagueError;
            setLeague(leagueData);

            // 2. Fetch Matches
            const { data: matchesData, error: matchesError } = await supabase
                .from('wk_matches')
                .select(`
                    id, 
                    kickoff_time, 
                    home_score, 
                    away_score, 
                    status,
                    home_team:home_team_id(id, name, flag_url),
                    away_team:away_team_id(id, name, flag_url)
                `)
                .order('kickoff_time', { ascending: true });
            if (matchesError) throw matchesError;
            setMatches(matchesData);

            // 3. Fetch My Predictions
            const { data: predsData, error: predsError } = await supabase
                .from('wk_predictions')
                .select('*')
                .eq('user_id', user.id);
            if (predsError) throw predsError;
            const predsMap = {};
            predsData.forEach(p => {
                predsMap[p.match_id] = { 
                    home: p.predicted_home_score, 
                    away: p.predicted_away_score, 
                    points: p.points_awarded,
                    isJoker: p.is_joker 
                };
            });
            setPredictions(predsMap);

            // 4. Fetch My Bonus Answers
            const { data: bonusData } = await supabase
                .from('wk_bonus_answers')
                .select('*')
                .eq('league_id', id)
                .eq('user_id', user.id)
                .maybeSingle();
            
            if (bonusData) {
                setBonusAnswers({
                    winner: bonusData.winner_prediction || '',
                    topscorer: bonusData.topscorer_prediction || ''
                });
            }

            // 5. Fetch Leaderboard via RPC
            const { data: boardData, error: boardError } = await supabase
                .rpc('get_league_leaderboard', { p_league_id: id });
            
            if (boardError) throw boardError;
            setLeaderboard(boardData || []);

        } catch (err) {
            console.error('Error fetching league data:', err);
        } finally {
            setLoading(false);
        }
    };

    const handlePredictionChange = (matchId, field, value) => {
        setHasUnsavedChanges(true);
        setPredictions(prev => ({
            ...prev,
            [matchId]: {
                ...prev[matchId],
                [field]: value === '' ? '' : parseInt(value, 10)
            }
        }));
    };

    const handleJokerToggle = (matchId) => {
        setHasUnsavedChanges(true);
        setPredictions(prev => {
            const isCurrentlyJoker = prev[matchId]?.isJoker || false;
            const totalJokers = Object.values(prev).filter(p => p.isJoker).length;

            // Enforce limit of 5 jokers
            if (!isCurrentlyJoker && totalJokers >= 5) {
                alert("🚨 Je mag maximaal 5 Jokers inzetten! Schakel eerst een andere Joker uit.");
                return prev;
            }

            return {
                ...prev,
                [matchId]: {
                    ...prev[matchId],
                    isJoker: !isCurrentlyJoker
                }
            };
        });
    };

    const savePredictions = async () => {
        setSaving(true);
        try {
            const upsertData = Object.entries(predictions)
                .filter(([matchId, p]) => {
                    // Beveiliging: Alleen opslaan/updaten als de wedstrijd nog niet is begonnen en scheduled is
                    const match = matches.find(m => m.id === matchId);
                    const isEditable = match && match.status === 'scheduled' && new Date(match.kickoff_time) > new Date();
                    return isEditable && p.home !== undefined && p.away !== undefined && p.home !== '' && p.away !== '';
                })
                .map(([matchId, p]) => ({
                    user_id: user.id,
                    match_id: matchId,
                    predicted_home_score: p.home,
                    predicted_away_score: p.away,
                    is_joker: p.isJoker || false
                }));

            if (upsertData.length > 0) {
                const { error } = await supabase
                    .from('wk_predictions')
                    .upsert(upsertData, { onConflict: 'user_id, match_id' });
                
                if (error) throw error;
            }

            // Save Bonus Answers
            const { error: bonusError } = await supabase
                .from('wk_bonus_answers')
                .upsert({
                    user_id: user.id,
                    league_id: id,
                    winner_prediction: bonusAnswers.winner,
                    topscorer_prediction: bonusAnswers.topscorer
                }, { onConflict: 'user_id, league_id' });
            
            if (bonusError) throw bonusError;

            setHasUnsavedChanges(false); 
            alert('Alles succesvol opgeslagen!');
        } catch (err) {
            console.error('Error saving predictions:', err);
            alert('Fout bij opslaan: ' + err.message);
        } finally {
            setSaving(false);
        }
    };

    // === LIVE STANDEN BEREKENEN ===
    const getGroupTables = () => {
        const tables = {};
        
        // Itereer over matches om teams te vinden en stats bij te werken
        matches.forEach(m => {
            const home = m.home_team;
            const away = m.away_team;
            if (!home || !away) return;

            const groupName = home.group_name || 'Overig';

            if (!tables[groupName]) tables[groupName] = {};
            
            if (!tables[groupName][home.id]) {
                tables[groupName][home.id] = { id: home.id, name: home.name, p: 0, w: 0, d: 0, l: 0, gf: 0, ga: 0, pts: 0 };
            }
            if (!tables[groupName][away.id]) {
                tables[groupName][away.id] = { id: away.id, name: away.name, p: 0, w: 0, d: 0, l: 0, gf: 0, ga: 0, pts: 0 };
            }

            if (m.status === 'finished' || (m.home_score !== null && m.away_score !== null)) {
                const statsH = tables[groupName][home.id];
                const statsA = tables[groupName][away.id];
                
                statsH.p++; statsA.p++;
                statsH.gf += m.home_score || 0; statsH.ga += m.away_score || 0;
                statsA.gf += m.away_score || 0; statsA.ga += m.home_score || 0;

                if (m.home_score > m.away_score) { statsH.w++; statsA.l++; statsH.pts += 3; }
                else if (m.away_score > m.home_score) { statsA.w++; statsH.l++; statsA.pts += 3; }
                else { statsH.d++; statsA.d++; statsH.pts += 1; statsA.pts += 1; }
            }
        });

        // Sorteer tables
        const sortedTables = {};
        Object.keys(tables).sort().forEach(grp => {
            sortedTables[grp] = Object.values(tables[grp]).sort((a, b) => {
                if (b.pts !== a.pts) return b.pts - a.pts; // 1. Punten
                const diffA = a.gf - a.ga;
                const diffB = b.gf - b.ga;
                if (diffB !== diffA) return diffB - diffA; // 2. Doelsaldo
                return b.gf - a.gf; // 3. Doelpunten vóór
            });
        });
        return sortedTables;
    };

    const handleUpgradeToPremium = async () => {
        setUpgradeLoading(true);
        try {
            // 🚀 PRODUCTIE: Stripe Price ID voor de WK Bedrijfspoule (€65 eenmalig)
            const PRICE_ID_WK_PREMIUM = "price_1TVrBsKcWBh9Je2KvRuxuFCM";

            const { data, error } = await supabase.functions.invoke('create-checkout', {
                body: {
                    priceId: PRICE_ID_WK_PREMIUM,
                    userId: user.id,
                    tenantId: null, // We upgrade a specific league, not a tenant
                    mode: 'payment', // EENMALIG, geen abonnement!
                    leagueId: id,
                    successUrl: window.location.origin + `/wkpoule/league/${id}?upgrade=success`,
                    cancelUrl: window.location.origin + `/wkpoule/league/${id}`
                }
            });

            if (error) throw error;
            if (data?.url) {
                window.location.href = data.url;
            }
        } catch (err) {
            console.error('Stripe error:', err);
            alert('Fout bij starten checkout: ' + err.message);
        } finally {
            setUpgradeLoading(false);
        }
    };

    const handleLogoUpload = async (e) => {
        const file = e.target.files[0];
        if (!file) return;
        if (!league?.is_premium) {
            alert("Alleen premium bedrijfspoules kunnen een logo instellen.");
            return;
        }

        setUploadingLogo(true);
        try {
            const fileExt = file.name.split('.').pop();
            const fileName = `wk_league_${id}_${Math.random()}.${fileExt}`;
            const filePath = `league-logos/${fileName}`;

            // Upload file
            const { error: uploadError } = await supabase.storage
                .from('logos') // Assuming 'logos' bucket exists
                .upload(filePath, file);

            if (uploadError) throw uploadError;

            const { data: { publicUrl } } = supabase.storage
                .from('logos')
                .getPublicUrl(filePath);

            // Update database
            const { error: updateErr } = await supabase
                .from('wk_leagues')
                .update({ logo_url: publicUrl })
                .eq('id', id);

            if (updateErr) throw updateErr;

            // Refresh league data
            fetchLeagueData();
            alert("Logo succesvol geüpload!");

        } catch (err) {
            console.error(err);
            alert("Fout bij uploaden: " + err.message);
        } finally {
            setUploadingLogo(false);
        }
    };

    if (loading) {
        return <div className="min-h-screen bg-black flex items-center justify-center">
            <div className="animate-spin rounded-full h-12 w-12 border-4 border-white/10 border-t-orange-500"></div>
        </div>;
    }

    return (
        <div className="min-h-screen bg-black text-white relative">
            {/* Header */}
            <nav className="border-b border-white/10 bg-black/50 backdrop-blur-md sticky top-0 z-50">
                <div className="max-w-5xl mx-auto px-6 h-20 flex items-center justify-between">
                    <div className="flex items-center gap-4">
                        <button onClick={() => navigate('/wkpoule/dashboard')} className="text-gray-400 hover:text-white transition-colors">
                            <ArrowLeft size={24} />
                        </button>
                        <div>
                            <div className="flex items-center gap-2">
                                <h1 className="font-bold text-xl flex items-center gap-2">
                                    {league?.is_premium && (
                                        league.logo_url ? 
                                        <img src={league.logo_url} alt="Logo" className="h-8 w-8 object-contain rounded bg-white/10" /> :
                                        <Crown size={20} className="text-yellow-500" />
                                    )}
                                    {league?.name}
                                </h1>
                                {league?.is_premium && <span className="bg-yellow-500/20 text-yellow-500 text-[10px] font-black px-1.5 py-0.5 rounded border border-yellow-500/30 uppercase">Bedrijfspoule</span>}
                            </div>
                            <button 
                                onClick={handleShare}
                                className="text-xs text-gray-500 flex items-center gap-1 hover:text-orange-400 transition-colors"
                            >
                                Invite Code: <span className="font-mono bg-white/5 px-1 rounded">{league?.invite_code}</span>
                                {copied ? <Check size={12} className="text-green-500" /> : <Copy size={12} />}
                            </button>
                        </div>
                    </div>
                    <div className="flex items-center gap-4">
                        <DarkModeToggleCompact />
                    </div>
                </div>
            </nav>

            <main className="max-w-5xl mx-auto px-6 py-8">
                
                {window.location.search.includes('upgrade=success') && (
                    <div className="bg-gradient-to-r from-green-900/80 to-emerald-900/80 border border-green-500/30 text-white p-6 mb-8 rounded-2xl shadow-2xl flex items-start gap-4 animate-slideUp">
                        <div className="bg-green-500 p-2 rounded-full text-white shadow-lg">
                            <Check size={24} />
                        </div>
                        <div>
                            <p className="font-black text-xl">Gefeliciteerd! 🎊</p>
                            <p className="text-emerald-200 text-sm mt-1">Je poule is nu officieel een Premium Bedrijfspoule. Je kunt direct je logo uploaden onder het tabblad "Beheer".</p>
                        </div>
                    </div>
                )}

                {/* Tabs */}
                <div className="flex gap-4 mb-8 border-b border-white/10 pb-4 overflow-x-auto">
                    <button 
                        className={`flex items-center gap-2 px-4 py-2 rounded-lg font-bold transition-all whitespace-nowrap ${activeTab === 'predictions' ? 'bg-orange-500 text-white' : 'text-gray-400 hover:bg-white/5'}`}
                        onClick={() => setActiveTab('predictions')}
                    >
                        Voorspellingen
                    </button>
                    <button 
                        className={`flex items-center gap-2 px-4 py-2 rounded-lg font-bold transition-all whitespace-nowrap ${activeTab === 'standings' ? 'bg-orange-500 text-white' : 'text-gray-400 hover:bg-white/5'}`}
                        onClick={() => setActiveTab('standings')}
                    >
                        <BarChart3 size={18} /> Standen
                    </button>
                    <button 
                        className={`flex items-center gap-2 px-4 py-2 rounded-lg font-bold transition-all whitespace-nowrap ${activeTab === 'leaderboard' ? 'bg-orange-500 text-white' : 'text-gray-400 hover:bg-white/5'}`}
                        onClick={() => setActiveTab('leaderboard')}
                    >
                        <Trophy size={18} /> Ranglijst
                    </button>
                    <button 
                        className={`flex items-center gap-2 px-4 py-2 rounded-lg font-bold transition-all whitespace-nowrap ${activeTab === 'rules' ? 'bg-orange-500 text-white' : 'text-gray-400 hover:bg-white/5'}`}
                        onClick={() => setActiveTab('rules')}
                    >
                        <div className="bg-white/10 p-0.5 rounded flex items-center justify-center text-[10px] border border-white/20">?</div> Spelregels
                    </button>
                    {league?.admin_id === user?.id && (
                        <button 
                            className={`flex items-center gap-2 px-4 py-2 rounded-lg font-bold transition-all whitespace-nowrap ${activeTab === 'settings' ? 'bg-orange-500 text-white' : 'text-gray-400 hover:bg-white/5'}`}
                            onClick={() => setActiveTab('settings')}
                        >
                            <Settings size={18} /> Beheer
                        </button>
                    )}
                </div>

                {activeTab === 'predictions' && (
                    <div className="animate-fadeIn">
                        
                        {/* 👑 SLIMME ADMIN-ONLY UPGRADE BANNER */}
                        {!league?.is_premium && league?.admin_id === user?.id && (
                            <div className="mb-8 bg-gradient-to-r from-indigo-900/50 to-purple-900/50 border border-indigo-500/30 rounded-2xl p-4 md:p-6 flex flex-col md:flex-row items-center justify-between gap-4 shadow-xl ring-1 ring-white/10">
                                <div className="flex items-center gap-4 text-center md:text-left">
                                    <div className="bg-yellow-400 p-2 rounded-xl text-black shadow-lg shrink-0">
                                        <Crown size={24} />
                                    </div>
                                    <div>
                                        <h4 className="font-black text-white flex items-center gap-2">Maak er een officiële Bedrijfspoule van! <span className="bg-green-500/20 text-green-400 text-[10px] font-black px-1.5 py-0.5 rounded">TIP</span></h4>
                                        <p className="text-indigo-200 text-xs mt-0.5">Upload je eigen bedrijfslogo, verwijder externe ads en krijg premium support voor €65,- eenmalig.</p>
                                    </div>
                                </div>
                                <Button 
                                    onClick={handleUpgradeToPremium}
                                    disabled={upgradeLoading}
                                    className="whitespace-nowrap bg-yellow-400 hover:bg-yellow-500 text-black font-black px-6 py-2.5 rounded-xl shadow-lg shadow-yellow-500/20 transition-all active:scale-95 shrink-0"
                                >
                                    {upgradeLoading ? 'Bezig...' : 'Nu Upgraden 🚀'}
                                </Button>
                            </div>
                        )}

                        <div className="flex flex-col md:flex-row justify-between items-start md:items-center mb-6 gap-4">
                            <div className="flex items-center gap-4">
                                <h2 className="text-2xl font-bold">Aankomende Wedstrijden</h2>
                                <div className={`flex items-center gap-1.5 px-3 py-1.5 rounded-full text-sm font-bold border ${Object.values(predictions).filter(p => p.isJoker).length === 5 ? 'bg-orange-500/20 border-orange-500 text-orange-400' : 'bg-white/5 border-white/10 text-gray-400'}`}>
                                    <Zap size={14} fill="currentColor" />
                                    <span>{Object.values(predictions).filter(p => p.isJoker).length}/5 Jokers</span>
                                </div>
                            </div>
                            <Button variant="primary" onClick={savePredictions} disabled={saving} icon={<Save size={18} />} className="bg-gradient-to-r from-orange-500 to-pink-500 w-full md:w-auto">
                                {saving ? 'Opslaan...' : 'Sla Alles Op'}
                            </Button>
                        </div>

                        {/* 🏆 DE GROTE BONUSVRAGEN KAART */}
                        <div className="mb-10 bg-gradient-to-br from-amber-600/20 to-amber-900/20 border border-amber-500/30 rounded-3xl p-6 md:p-8 relative overflow-hidden shadow-2xl shadow-amber-900/20">
                            <div className="absolute top-0 right-0 text-amber-500/10 -mt-4 -mr-4 rotate-12">
                                <Crown size={120} fill="currentColor" />
                            </div>
                            
                            <div className="relative z-10">
                                <div className="flex items-center gap-3 mb-6">
                                    <div className="bg-gradient-to-br from-amber-400 to-orange-500 p-3 rounded-2xl text-black shadow-lg shadow-orange-500/30">
                                        <Trophy size={24} />
                                    </div>
                                    <div>
                                        <h3 className="text-2xl font-black text-white tracking-tight">De Grote Bonusvragen</h3>
                                        <p className="text-amber-200/80 text-xs font-medium">Wie sleept de hoofdprijzen in de wacht? (+ bonuspunten!)</p>
                                    </div>
                                </div>

                                <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
                                    {/* Vraag 1: Kampioen */}
                                    <div className="bg-black/40 backdrop-blur-md rounded-2xl p-4 border border-white/5 focus-within:border-amber-500/50 transition-colors">
                                        <label className="text-xs font-black uppercase tracking-wider text-amber-400 mb-2 block">Wereldkampioen 2026</label>
                                        <input 
                                            type="text"
                                            placeholder="Welk land pakt de titel?"
                                            value={bonusAnswers.winner}
                                            onChange={(e) => { setBonusAnswers(prev => ({...prev, winner: e.target.value})); setHasUnsavedChanges(true); }}
                                            disabled={matches.some(m => new Date(m.kickoff_time) < new Date())}
                                            className="w-full bg-transparent border-none outline-none text-white placeholder-gray-600 font-bold text-lg p-0"
                                        />
                                    </div>

                                    {/* Vraag 2: Topscorer */}
                                    <div className="bg-black/40 backdrop-blur-md rounded-2xl p-4 border border-white/5 focus-within:border-amber-500/50 transition-colors">
                                        <label className="text-xs font-black uppercase tracking-wider text-amber-400 mb-2 block">Topscorer van het Toernooi</label>
                                        <input 
                                            type="text"
                                            placeholder="Naam van de speler"
                                            value={bonusAnswers.topscorer}
                                            onChange={(e) => { setBonusAnswers(prev => ({...prev, topscorer: e.target.value})); setHasUnsavedChanges(true); }}
                                            disabled={matches.some(m => new Date(m.kickoff_time) < new Date())}
                                            className="w-full bg-transparent border-none outline-none text-white placeholder-gray-600 font-bold text-lg p-0"
                                        />
                                    </div>
                                </div>
                                
                                {matches.some(m => new Date(m.kickoff_time) < new Date()) ? (
                                    <p className="text-[10px] text-red-400 mt-4 flex items-center gap-1 font-bold"><Crown size={10}/> Invoer is vergrendeld, het WK is begonnen!</p>
                                ) : (
                                    <p className="text-[10px] text-amber-200/50 mt-4 flex items-center gap-1 font-bold italic">ℹ️ Te wijzigen tot aan de allereerste wedstrijd.</p>
                                )}
                            </div>
                        </div>

                        {matches.length === 0 ? (
                            <div className="text-center py-12 text-gray-400 bg-white/5 rounded-2xl border border-white/10">
                                Er zijn nog geen WK wedstrijden ingeladen in het systeem.
                            </div>
                        ) : (
                            <div className="space-y-10">
                                {Object.entries(
                                    matches.reduce((groups, match) => {
                                        const dateStr = new Date(match.kickoff_time).toLocaleDateString('nl-NL', { weekday: 'long', day: 'numeric', month: 'long' });
                                        if (!groups[dateStr]) groups[dateStr] = [];
                                        groups[dateStr].push(match);
                                        return groups;
                                    }, {})
                                ).map(([dateLabel, dayMatches]) => (
                                    <div key={dateLabel}>
                                        <h3 className="text-lg font-black text-orange-500 mb-4 uppercase tracking-wider sticky top-[80px] bg-black/90 backdrop-blur-sm py-2 z-10 border-b border-white/5">{dateLabel}</h3>
                                        <div className="grid gap-3">
                                            {dayMatches.map(match => (
                                                <div key={match.id} className="bg-white/5 border border-white/10 rounded-2xl p-4 flex flex-col md:flex-row items-center gap-4 hover:border-orange-500/30 hover:bg-white/10 transition-all">
                                                    <div className="text-sm font-bold text-gray-400 w-full md:w-20 text-center md:text-left">
                                                        {new Date(match.kickoff_time).toLocaleTimeString('nl-NL', { hour: '2-digit', minute:'2-digit' })}
                                                    </div>
                                                    
                                                    <div className="flex-1 flex justify-center items-center gap-6 w-full">
                                                        {/* Home Team */}
                                                        <div className="flex flex-col items-center w-28">
                                                            <div className="w-12 h-12 rounded-full bg-white/5 border border-white/10 mb-2 overflow-hidden flex items-center justify-center shadow-inner">
                                                                {match.home_team?.flag_url ? 
                                                                    <img src={match.home_team.flag_url} alt="" className="w-full h-full object-cover" /> :
                                                                    <span className="text-xs font-bold text-gray-500">{match.home_team?.name}</span>
                                                                }
                                                            </div>
                                                            <span className="font-bold text-sm text-center text-gray-100">
                                                                {t(`wk_poule.countries.${match.home_team?.name}`, match.home_team?.name)}
                                                            </span>
                                                        </div>

                                                        {/* Joker Toggle */}
                                                        <div className="flex flex-col items-center justify-center">
                                                            <button
                                                                type="button"
                                                                onClick={() => handleJokerToggle(match.id)}
                                                                disabled={match.status !== 'scheduled'}
                                                                title={predictions[match.id]?.isJoker ? "Joker actief! (2x punten)" : "Zet Joker in"}
                                                                className={`p-2.5 rounded-xl transition-all flex flex-col items-center group relative ${predictions[match.id]?.isJoker ? 'bg-gradient-to-br from-orange-400 to-orange-600 text-white shadow-lg shadow-orange-500/30 ring-2 ring-white/20 scale-110' : 'bg-white/5 text-gray-500 hover:text-orange-400 hover:bg-white/10 hover:scale-105'} ${match.status !== 'scheduled' ? 'opacity-50 cursor-not-allowed' : ''}`}
                                                            >
                                                                <Zap size={20} fill={predictions[match.id]?.isJoker ? "currentColor" : "none"} className={predictions[match.id]?.isJoker ? "animate-pulse" : ""} />
                                                            </button>
                                                        </div>

                                                        {/* Score Inputs & Actual Score Display */}
                                                        <div className="flex flex-col items-center gap-1.5">
                                                            <div className="flex items-center gap-3 bg-black/40 p-2 rounded-2xl border border-white/5">
                                                                <input 
                                                                    type="number" 
                                                                    min="0" max="20"
                                                                    className="w-12 h-12 bg-white/10 border border-white/10 rounded-xl text-center text-xl font-black text-white focus:border-orange-500 focus:bg-white/20 focus:ring-2 focus:ring-orange-500/20 transition-all outline-none"
                                                                    value={predictions[match.id]?.home ?? ''}
                                                                    onChange={(e) => handlePredictionChange(match.id, 'home', e.target.value)}
                                                                    disabled={match.status !== 'scheduled'}
                                                                    placeholder="-"
                                                                />
                                                                <span className="text-gray-600 font-black">:</span>
                                                                <input 
                                                                    type="number" 
                                                                    min="0" max="20"
                                                                    className="w-12 h-12 bg-white/10 border border-white/10 rounded-xl text-center text-xl font-black text-white focus:border-orange-500 focus:bg-white/20 focus:ring-2 focus:ring-orange-500/20 transition-all outline-none"
                                                                    value={predictions[match.id]?.away ?? ''}
                                                                    onChange={(e) => handlePredictionChange(match.id, 'away', e.target.value)}
                                                                    disabled={match.status !== 'scheduled'}
                                                                    placeholder="-"
                                                                />
                                                            </div>
                                                            {match.status !== 'scheduled' && (match.home_score !== null || match.away_score !== null) && (
                                                                <div className="text-[10px] font-bold text-orange-400 uppercase tracking-wider bg-orange-500/10 border border-orange-500/20 px-2 py-0.5 rounded-md">
                                                                    Uitslag: {match.home_score} - {match.away_score}
                                                                </div>
                                                            )}
                                                        </div>

                                                        {/* Away Team */}
                                                        <div className="flex flex-col items-center w-28">
                                                            <div className="w-12 h-12 rounded-full bg-white/5 border border-white/10 mb-2 overflow-hidden flex items-center justify-center shadow-inner">
                                                                {match.away_team?.flag_url ? 
                                                                    <img src={match.away_team.flag_url} alt="" className="w-full h-full object-cover" /> :
                                                                    <span className="text-xs font-bold text-gray-500">{match.away_team?.name}</span>
                                                                }
                                                            </div>
                                                            <span className="font-bold text-sm text-center text-gray-100">
                                                                {t(`wk_poule.countries.${match.away_team?.name}`, match.away_team?.name)}
                                                            </span>
                                                        </div>
                                                    </div>

                                                    <div className="w-full md:w-24 flex justify-center md:justify-end">
                                                        {match.status === 'finished' ? (
                                                            <div className="bg-orange-500/10 border border-orange-500/20 rounded-xl p-2 w-full text-center">
                                                                <div className="text-[10px] uppercase tracking-wider font-bold text-orange-400">Punten</div>
                                                                <div className="font-black text-orange-500 text-xl leading-tight">+{predictions[match.id]?.points || 0}</div>
                                                            </div>
                                                        ) : (
                                                            <span className="text-xs text-emerald-400 bg-emerald-500/10 border border-emerald-500/20 font-bold px-3 py-1 rounded-full shadow-sm">
                                                                Actief
                                                            </span>
                                                        )}
                                                    </div>
                                                </div>
                                            ))}
                                        </div>
                                    </div>
                                ))}
                            </div>
                        )}
                    </div>
                )}

                {/* === LIVE STANDEN TAB === */}
                {activeTab === 'standings' && (
                    <div className="animate-fadeIn">
                        <div className="mb-8 text-center">
                            <h2 className="text-3xl font-black mb-2">📊 Officiële Groepsstanden</h2>
                            <p className="text-gray-400 text-sm">Live stand gebaseerd op de gespeelde wedstrijden.</p>
                        </div>

                        {Object.keys(getGroupTables()).length === 0 ? (
                            <div className="text-center py-10 text-gray-500 bg-white/5 rounded-2xl border border-white/10">
                                Geen data beschikbaar om standen te berekenen.
                            </div>
                        ) : (
                            <div className="grid grid-cols-1 lg:grid-cols-2 gap-6 pb-20">
                                {Object.entries(getGroupTables()).map(([groupName, teams]) => (
                                    <div key={groupName} className="bg-white/5 border border-white/10 rounded-2xl overflow-hidden">
                                        <div className="bg-gradient-to-r from-orange-500/10 to-transparent px-4 py-3 border-b border-white/5 flex justify-between items-center">
                                            <h3 className="font-black text-orange-400 uppercase tracking-wider text-sm">{groupName}</h3>
                                            <span className="text-[10px] font-bold text-gray-500">DOOR NAAR 1/8 FINALE → TOP 2</span>
                                        </div>
                                        <div className="p-2">
                                            <table className="w-full text-sm">
                                                <thead>
                                                    <tr className="text-[10px] text-gray-500 font-black uppercase tracking-widest text-left">
                                                        <th className="px-3 py-2 w-8">#</th>
                                                        <th className="px-3 py-2">Land</th>
                                                        <th className="px-3 py-2 text-center">G</th>
                                                        <th className="px-3 py-2 text-center hidden md:table-cell">DS</th>
                                                        <th className="px-3 py-2 text-right font-bold text-white">Pnt</th>
                                                    </tr>
                                                </thead>
                                                <tbody>
                                                    {teams.map((team, idx) => {
                                                        const isTop2 = idx < 2;
                                                        const ds = team.gf - team.ga;
                                                        return (
                                                            <tr key={team.id} className={`border-b border-white/5 last:border-0 ${isTop2 ? 'bg-green-500/5' : ''}`}>
                                                                <td className={`px-3 py-3 font-bold text-center w-8 ${isTop2 ? 'text-green-400' : 'text-gray-600'}`}>
                                                                    {idx + 1}
                                                                </td>
                                                                <td className="px-3 py-3">
                                                                    <div className="flex items-center gap-2 font-bold">
                                                                        <div className="w-6 h-4 bg-gray-800 rounded overflow-hidden border border-white/10 flex items-center justify-center text-[8px] text-gray-500 uppercase font-black shrink-0">
                                                                            {team.name.substring(0, 2)}
                                                                        </div>
                                                                        <span className={isTop2 ? 'text-white' : 'text-gray-400'}>{t(`wk_poule.countries.${team.name}`, team.name)}</span>
                                                                    </div>
                                                                </td>
                                                                <td className="px-3 py-3 text-center text-gray-400 font-mono">{team.p}</td>
                                                                <td className={`px-3 py-3 text-center font-mono hidden md:table-cell ${ds > 0 ? 'text-green-400' : ds < 0 ? 'text-red-400' : 'text-gray-500'}`}>
                                                                    {ds > 0 ? `+${ds}` : ds}
                                                                </td>
                                                                <td className={`px-3 py-3 text-right font-black text-base ${isTop2 ? 'text-green-400' : 'text-white'}`}>
                                                                    {team.pts}
                                                                </td>
                                                            </tr>
                                                        );
                                                    })}
                                                </tbody>
                                            </table>
                                        </div>
                                    </div>
                                ))}
                            </div>
                        )}
                    </div>
                )}

                {activeTab === 'leaderboard' && (
                    <div className="animate-fadeIn max-w-2xl mx-auto">
                        <div className="bg-white/5 border border-white/10 rounded-2xl overflow-hidden">
                            <div className="p-4 bg-white/5 border-b border-white/10 grid grid-cols-12 gap-4 font-bold text-sm text-gray-400">
                                <div className="col-span-2 text-center">#</div>
                                <div className="col-span-7">Deelnemer</div>
                                <div className="col-span-3 text-right">Punten</div>
                            </div>
                            <div className="divide-y divide-white/5">
                                {leaderboard.map((entry, index) => (
                                    <div 
                                        key={entry.user_id} 
                                        onClick={() => fetchCompetitorPredictions(entry)}
                                        className={`p-4 grid grid-cols-12 gap-4 items-center cursor-pointer transition-all border-l-4 border-transparent hover:bg-white/10 hover:border-orange-500 ${entry.user_id === user.id ? 'bg-orange-500/5 border-orange-500/50' : ''}`}
                                    >
                                        <div className="col-span-2 flex justify-center">
                                            {index === 0 ? <Trophy className="text-yellow-500" size={24} /> : 
                                             index === 1 ? <Trophy className="text-gray-400" size={24} /> : 
                                             index === 2 ? <Trophy className="text-amber-700" size={24} /> : 
                                             <span className="text-gray-500 font-bold">{index + 1}</span>}
                                        </div>
                                        <div className="col-span-7 font-bold flex items-center gap-2">
                                            <div className="flex flex-col">
                                                <div className="flex items-center gap-2">
                                                    <span className="text-gray-100">{entry.full_name}</span>
                                                    {entry.user_id === user.id && <span className="text-[10px] bg-orange-500 text-white px-1.5 py-0.5 font-black rounded uppercase tracking-wider">Jij</span>}
                                                </div>
                                                <span className="text-[10px] text-gray-500 flex items-center gap-1 mt-0.5"><Eye size={10}/> Klik om voorspellingen te zien</span>
                                            </div>
                                        </div>
                                        <div className="col-span-3 text-right font-black text-xl text-orange-500">
                                            {entry.total_points}
                                        </div>
                                    </div>
                                ))}
                            </div>
                        </div>
                        <div className="mt-8 flex flex-col sm:flex-row gap-4 justify-center items-center">
                            <Button 
                                variant="outline" 
                                className={`transition-all w-full sm:w-auto ${copied ? 'border-green-500 text-green-500' : 'border-white/20 text-white'}`}
                                icon={copied ? <Check size={18} /> : <Share2 size={18} />}
                                onClick={handleShare}
                            >
                                {copied ? 'Gekopieerd naar klembord!' : 'Kopieer Uitnodigingslink'}
                            </Button>
                            
                            <button 
                                onClick={handleWhatsAppShare}
                                className="flex items-center justify-center gap-2 bg-[#25D366] hover:bg-[#20ba5a] text-white font-bold px-6 py-2.5 rounded-xl w-full sm:w-auto transition-all shadow-lg shadow-[#25D366]/20 active:scale-95"
                            >
                                <svg viewBox="0 0 24 24" width="20" height="20" fill="currentColor">
                                    <path d="M.057 24l1.687-6.163c-1.041-1.804-1.588-3.849-1.587-5.946.003-6.556 5.338-11.891 11.893-11.891 3.181.001 6.167 1.24 8.413 3.488 2.245 2.248 3.481 5.236 3.48 8.414-.003 6.557-5.338 11.892-11.893 11.892-1.99-.001-3.951-.5-5.688-1.448l-6.305 1.654zm6.597-3.807c1.676.995 3.276 1.591 5.392 1.592 5.448 0 9.886-4.434 9.889-9.885.002-5.462-4.415-9.89-9.881-9.892-5.452 0-9.887 4.434-9.889 9.884-.001 2.225.651 3.891 1.746 5.634l-.999 3.648 3.742-.981zm11.387-5.464c-.074-.124-.272-.198-.57-.347-.297-.149-1.758-.868-2.031-.967-.272-.099-.47-.149-.669.149-.198.297-.768.967-.941 1.165-.173.198-.347.223-.644.074-.297-.149-1.255-.462-2.39-1.475-.883-.788-1.48-1.761-1.653-2.059-.173-.297-.018-.458.13-.606.134-.133.297-.347.446-.521.151-.172.2-.296.3-.495.099-.198.05-.372-.025-.521-.075-.148-.669-1.611-.916-2.206-.242-.579-.487-.501-.669-.512-.173-.009-.371-.01-.57-.01-.198 0-.52.074-.792.372s-1.04 1.016-1.04 2.479 1.065 2.876 1.213 3.074c.149.198 2.095 3.2 5.076 4.487.709.306 1.263.489 1.694.626.712.226 1.36.194 1.872.118.571-.085 1.758-.719 2.006-1.413.248-.695.248-1.29.173-1.414z"/>
                                </svg>
                                Delen via WhatsApp
                            </button>
                        </div>
                    </div>
                )}

                {activeTab === 'rules' && (
                    <div className="animate-fadeIn max-w-4xl mx-auto pb-20">
                        {/* 👑 Mega Header */}
                        <div className="text-center mb-12">
                            <div className="inline-flex items-center justify-center bg-orange-500/20 text-orange-500 p-4 rounded-3xl mb-6 ring-1 ring-orange-500/30">
                                <Trophy size={40} />
                            </div>
                            <h1 className="text-4xl md:text-5xl font-black bg-gradient-to-b from-white to-gray-400 bg-clip-text text-transparent mb-4">Ultieme WK Handleiding</h1>
                            <p className="text-lg text-gray-400 max-w-2xl mx-auto leading-relaxed">Alles wat je moet weten om mee te doen, te winnen, en de heerser van jouw poule te worden.</p>
                        </div>

                        <div className="space-y-12">
                            
                            {/* SECTIE 1: HET STAPPENPLAN */}
                            <section>
                                <div className="flex items-center gap-3 mb-6">
                                    <div className="w-8 h-8 rounded-lg bg-white/10 flex items-center justify-center text-white font-black">1</div>
                                    <h2 className="text-2xl font-bold">In 3 Stappen Meedoen</h2>
                                </div>
                                <div className="grid grid-cols-1 md:grid-cols-3 gap-6">
                                    <div className="bg-white/5 border border-white/10 rounded-3xl p-6 relative">
                                        <div className="text-4xl font-black text-white/10 absolute right-6 top-6">01</div>
                                        <h3 className="text-lg font-bold text-white mb-2">Poule Betreden</h3>
                                        <p className="text-sm text-gray-400">Je bent via een unieke link of invite-code uitgenodigd door de organisator van deze poule. Je staat nu officieel op de deelnemerslijst.</p>
                                    </div>
                                    <div className="bg-white/5 border border-white/10 rounded-3xl p-6 relative">
                                        <div className="text-4xl font-black text-white/10 absolute right-6 top-6">02</div>
                                        <h3 className="text-lg font-bold text-white mb-2">Voorspellen</h3>
                                        <p className="text-sm text-gray-400">Ga naar het tabblad "Voorspellingen". Vul voor alle komende wedstrijden de uitslag in na de reguliere 90 minuten.</p>
                                    </div>
                                    <div className="bg-white/5 border border-white/10 rounded-3xl p-6 relative ring-2 ring-orange-500/20 bg-orange-500/5">
                                        <div className="text-4xl font-black text-orange-500/20 absolute right-6 top-6">03</div>
                                        <h3 className="text-lg font-bold text-white mb-2">Opslaan</h3>
                                        <p className="text-sm text-gray-400">Vergeet NOOIT op de grote knop <strong className="text-orange-400">"Sla Alles Op"</strong> te drukken! Pas dan staan je keuzes veilig in de database.</p>
                                    </div>
                                </div>
                            </section>

                            {/* SECTIE 2: DE PUNTENTELLING (Visueel) */}
                            <section>
                                <div className="flex items-center gap-3 mb-6">
                                    <div className="w-8 h-8 rounded-lg bg-white/10 flex items-center justify-center text-white font-black">2</div>
                                    <h2 className="text-2xl font-bold">Tactiek & Puntentelling</h2>
                                </div>
                                
                                <div className="grid grid-cols-1 lg:grid-cols-3 gap-6">
                                    {/* Toto */}
                                    <div className="lg:col-span-2 bg-white/5 border border-white/10 rounded-3xl overflow-hidden flex flex-col">
                                        <div className="p-6 bg-white/5 border-b border-white/10">
                                            <h3 className="text-xl font-bold">Basis Punten per Wedstrijd</h3>
                                            <p className="text-xs text-gray-500">Wat levert een voorspelling je op?</p>
                                        </div>
                                        <div className="p-6 space-y-4 flex-1 bg-black/20">
                                            <div className="flex items-center justify-between bg-black/40 p-4 rounded-2xl border border-emerald-500/20">
                                                <div>
                                                    <div className="font-black text-white">Exacte Uitslag</div>
                                                    <div className="text-xs text-gray-400">Je hebt beide getallen exact goed.</div>
                                                </div>
                                                <div className="bg-emerald-500 text-black px-4 py-2 rounded-xl font-black text-xl shadow-lg shadow-emerald-500/20">+10 pnt</div>
                                            </div>

                                            <div className="flex items-center justify-between bg-black/40 p-4 rounded-2xl border border-blue-500/20">
                                                <div>
                                                    <div className="font-black text-white">Correcte Winnaar (Toto)</div>
                                                    <div className="text-xs text-gray-400">De winnaar (of gelijkspel) is goed, uitslag verschilt.</div>
                                                </div>
                                                <div className="bg-blue-500 text-white px-4 py-2 rounded-xl font-black text-xl">+5 pnt</div>
                                            </div>

                                            <div className="flex items-center justify-between bg-black/40 p-4 rounded-2xl border border-white/5 opacity-50">
                                                <div>
                                                    <div className="font-black text-gray-400">Verkeerde Winnaar</div>
                                                    <div className="text-xs text-gray-500">Helaas, de verkeerde kant op voorspeld.</div>
                                                </div>
                                                <div className="bg-gray-700 text-gray-400 px-4 py-2 rounded-xl font-black text-xl">0 pnt</div>
                                            </div>
                                        </div>
                                    </div>

                                    {/* De Joker Kart */}
                                    <div className="bg-gradient-to-br from-orange-600 to-pink-600 rounded-3xl p-1 shadow-2xl shadow-orange-500/20">
                                        <div className="bg-[#0d0d0d] w-full h-full rounded-[22px] p-6 flex flex-col relative overflow-hidden">
                                            <div className="absolute top-0 right-0 p-4 opacity-10"><Zap size={100} fill="currentColor" className="text-white" /></div>
                                            
                                            <div className="w-12 h-12 rounded-xl bg-gradient-to-br from-orange-400 to-pink-500 flex items-center justify-center text-white mb-6 shadow-lg shadow-orange-500/30">
                                                <Zap size={24} fill="currentColor" />
                                            </div>
                                            
                                            <h3 className="text-2xl font-black text-white mb-2">De Joker ⚡</h3>
                                            <p className="text-gray-400 text-sm leading-relaxed flex-1">
                                                Zet de joker in op maximaal **5 wedstrijden** waar je extreem zeker van bent. Behaalde punten worden direct verdubbeld!
                                            </p>
                                            
                                            <div className="mt-6 p-4 bg-white/5 border border-white/10 rounded-2xl flex items-center justify-between">
                                                <div className="text-xs font-bold uppercase tracking-widest text-gray-400">Max Score</div>
                                                <div className="text-2xl font-black bg-gradient-to-r from-orange-400 to-pink-400 bg-clip-text text-transparent">20 PNT</div>
                                            </div>
                                        </div>
                                    </div>
                                </div>
                            </section>

                            {/* SECTIE 3: WEDSTRIJDREGELS & DEADLINES */}
                            <section className="bg-white/5 border border-white/10 rounded-3xl p-8">
                                <div className="flex items-center gap-3 mb-8">
                                    <div className="w-8 h-8 rounded-lg bg-white/10 flex items-center justify-center text-white font-black">3</div>
                                    <h2 className="text-2xl font-bold">De Keiharde Spelregels</h2>
                                </div>

                                <div className="grid grid-cols-1 md:grid-cols-2 gap-10">
                                    <div>
                                        <h4 className="flex items-center gap-2 font-black text-orange-400 mb-4">
                                            <span className="w-2 h-2 rounded-full bg-orange-500"></span> Deadline & Aanpassen
                                        </h4>
                                        <ul className="space-y-3 text-sm text-gray-300">
                                            <li>• Je kunt voorspellingen invullen en aanpassen tot <strong>één minuut voor de officiële aftrap</strong>.</li>
                                            <li>• Zodra een wedstrijd de status "Live" krijgt, wordt de invoer vergrendeld.</li>
                                            <li>• Je mag gedurende het hele toernooi bepalen wanneer je je 5 jokers inzet.</li>
                                        </ul>
                                    </div>
                                    <div>
                                        <h4 className="flex items-center gap-2 font-black text-orange-400 mb-4">
                                            <span className="w-2 h-2 rounded-full bg-orange-500"></span> Uitslagbepaling
                                        </h4>
                                        <ul className="space-y-3 text-sm text-gray-300">
                                            <li>• Wij rekenen altijd met de stand na de <strong>officiële reguliere speeltijd</strong> (meestal 90 minuten plus blessuretijd).</li>
                                            <li>• Eventuele verlengingen (extra time) of strafschoppenseries tellen <strong>NIET</strong> mee voor de uitslag in deze poule.</li>
                                            <li>• De database gebruikt de officiële data feed, dus geen ruimte voor discussie over de score.</li>
                                        </ul>
                                    </div>
                                </div>
                            </section>

                            {/* SECTIE 4: ORGANISATIE & PRIJZEN */}
                            <section>
                                <div className="flex items-center gap-3 mb-6">
                                    <div className="w-8 h-8 rounded-lg bg-white/10 flex items-center justify-center text-white font-black">4</div>
                                    <h2 className="text-2xl font-bold">Organisatie & Prijzen</h2>
                                </div>
                                <div className="bg-gradient-to-r from-blue-900/30 to-indigo-900/30 border border-blue-500/20 rounded-3xl p-8 flex flex-col md:flex-row items-center gap-8 shadow-xl">
                                    <div className="flex-1">
                                        <h3 className="text-xl font-bold text-blue-300 mb-3">Belangrijk over Prijzenpotjes</h3>
                                        <p className="text-gray-300 text-sm leading-relaxed mb-4">
                                            ProPoule is een **administratief hulpmiddel** dat het rekenen en de stand bijhoudt. Wij beheren géén prijzengeld en handelen geen betalingen af tussen deelnemers.
                                        </p>
                                        <p className="text-gray-300 text-sm leading-relaxed">
                                            De organisatie van deze specifieke poule ligt volledig in handen van de beheerder die jou heeft uitgenodigd. Eventuele inleggelden of prijzen (zoals 55% voor de winnaar) dienen **onderling geregeld te worden** buiten dit platform om.
                                        </p>
                                    </div>
                                    <div className="w-full md:w-64 bg-black/40 rounded-2xl border border-white/10 p-6 text-center">
                                        <div className="text-4xl mb-2">💸</div>
                                        <div className="font-bold text-white text-sm uppercase tracking-wider mb-2">Deelnemers-geld</div>
                                        <p className="text-xs text-gray-400">Regel dit via Tikkie of contant direct met de beheerder van jouw poule.</p>
                                    </div>
                                </div>
                            </section>

                            {/* SECTIE 5: FAQ VEELGESTELDE VRAGEN */}
                            <section>
                                <div className="flex items-center gap-3 mb-6">
                                    <div className="w-8 h-8 rounded-lg bg-white/10 flex items-center justify-center text-white font-black">5</div>
                                    <h2 className="text-2xl font-bold">Veelgestelde Vragen</h2>
                                </div>
                                <div className="space-y-4">
                                    <details className="group bg-white/5 border border-white/10 rounded-2xl p-6 [&_summary::-webkit-details-marker]:hidden">
                                        <summary className="flex items-center justify-between cursor-pointer">
                                            <h3 className="font-bold text-lg text-gray-100 group-open:text-orange-400 transition-colors">Wanneer wordt de stand bijgewerkt?</h3>
                                            <span className="text-2xl text-gray-500 group-open:rotate-45 transition-transform">+</span>
                                        </summary>
                                        <p className="text-gray-400 text-sm mt-4 leading-relaxed">
                                            Zodra het eindsignaal van een wedstrijd klinkt en de officiële uitslag door de scheidsrechter is bevestigd, zal ons systeem binnen enkele minuten alle punten voor de complete poule herberekenen. De ranglijst verspringt dan live!
                                        </p>
                                    </details>

                                    <details className="group bg-white/5 border border-white/10 rounded-2xl p-6 [&_summary::-webkit-details-marker]:hidden">
                                        <summary className="flex items-center justify-between cursor-pointer">
                                            <h3 className="font-bold text-lg text-gray-100 group-open:text-orange-400 transition-colors">Kan ik spieken bij mijn tegenstanders?</h3>
                                            <span className="text-2xl text-gray-500 group-open:rotate-45 transition-transform">+</span>
                                        </summary>
                                        <p className="text-gray-400 text-sm mt-4 leading-relaxed">
                                            Ja en nee! Je kunt op het tabblad "Ranglijst" op de naam van een ander klikken om zijn/haar profiel te zien. ECHTER: Voorspellingen voor toekomstige wedstrijden zijn <strong>verborgen</strong> (met een slotje) totdat de wedstrijd begint. Dit voorkomt dat spelers elkaar kunnen nadoen. Zodra het fluitsignaal klinkt, worden alle kaarten op tafel gelegd.
                                        </p>
                                    </details>

                                    <details className="group bg-white/5 border border-white/10 rounded-2xl p-6 [&_summary::-webkit-details-marker]:hidden">
                                        <summary className="flex items-center justify-between cursor-pointer">
                                            <h3 className="font-bold text-lg text-gray-100 group-open:text-orange-400 transition-colors">Ik ben een wedstrijd vergeten in te vullen, wat nu?</h3>
                                            <span className="text-2xl text-gray-500 group-open:rotate-45 transition-transform">+</span>
                                        </summary>
                                        <p className="text-gray-400 text-sm mt-4 leading-relaxed">
                                            Helaas. Een niet ingevulde wedstrijd levert automatisch 0 punten op. Je kunt de rest van het toernooi natuurlijk wel gewoon verder spelen. Tip: vul aan het begin van de week vast alles in, je kunt het later altijd nog wijzigen!
                                        </p>
                                    </details>
                                </div>
                            </section>
                        </div>
                        
                        <div className="mt-20 text-center border-t border-white/10 pt-10 text-gray-500 text-sm">
                            Dit reglement is automatisch gegenereerd door ProPoule &copy; {new Date().getFullYear()}
                        </div>
                    </div>
                )}

                {activeTab === 'settings' && league?.admin_id === user?.id && (
                    <div className="animate-fadeIn max-w-2xl mx-auto space-y-8">
                        
                        {/* 🎁 UPGRADE CALLOUT - ALTIJD BOVENAAN ALS NIET PREMIUM */}
                        {!league?.is_premium && (
                            <div className="bg-gradient-to-r from-indigo-900/80 to-purple-900/80 border border-indigo-500/30 rounded-3xl p-8 shadow-2xl relative overflow-hidden group">
                                <div className="absolute -right-10 -top-10 w-40 h-40 bg-purple-500 rounded-full blur-3xl opacity-30 group-hover:opacity-50 transition-opacity"></div>
                                <div className="relative z-10">
                                    <div className="flex items-center gap-3 mb-4">
                                        <div className="bg-yellow-400 p-2 rounded-xl text-black shadow-lg shadow-yellow-400/20">
                                            <Crown size={24} />
                                        </div>
                                        <h3 className="text-2xl font-black text-white">Maak er een Bedrijfspoule van!</h3>
                                    </div>
                                    <p className="text-purple-200 mb-6 leading-relaxed">
                                        Wil je deze poule professionaliseren voor jouw bedrijf of club? Upgrade eenmalig en ontgrendel premium functies:
                                    </p>
                                    <ul className="space-y-2 mb-8 text-white/80 font-medium text-sm">
                                        <li className="flex items-center gap-2"><Check size={16} className="text-green-400"/> Eigen Bedrijfslogo bovenaan de pagina</li>
                                        <li className="flex items-center gap-2"><Check size={16} className="text-green-400"/> Verwijder alle externe advertenties / links</li>
                                        <li className="flex items-center gap-2"><Check size={16} className="text-green-400"/> Exporteer uitslagen naar Excel (Binnenkort)</li>
                                    </ul>
                                    
                                    <div className="flex flex-col sm:flex-row items-center gap-4">
                                        <Button 
                                            onClick={handleUpgradeToPremium}
                                            disabled={upgradeLoading}
                                            className="w-full sm:w-auto bg-yellow-400 hover:bg-yellow-500 text-black font-black px-8 py-4 text-lg rounded-xl shadow-xl shadow-yellow-500/20 transition-all active:scale-95"
                                        >
                                            {upgradeLoading ? 'Bezig...' : 'Nu Upgraden naar Bedrijfspoule'}
                                        </Button>
                                        <div className="text-sm text-purple-200">
                                            Eenmalige investering
                                        </div>
                                    </div>
                                </div>
                            </div>
                        )}

                        {/* 🚀 PREMIUM SETTINGS AREA */}
                        {league?.is_premium ? (
                            <div className="bg-white/5 border border-white/10 rounded-3xl p-8">
                                <div className="flex items-center gap-3 mb-6">
                                    <div className="bg-green-500/20 p-2 rounded-lg text-green-400">
                                        <ImageIcon size={20} />
                                    </div>
                                    <h3 className="text-xl font-bold">Branding & Logo</h3>
                                </div>
                                <p className="text-gray-400 text-sm mb-6">Upload het logo van je bedrijf of club om de WK Poule te personaliseren.</p>
                                
                                <div className="flex items-center gap-6">
                                    <div className="w-32 h-32 bg-black/50 border-2 border-dashed border-white/20 rounded-2xl flex flex-col items-center justify-center relative overflow-hidden group">
                                        {league.logo_url ? (
                                            <img src={league.logo_url} alt="Current Logo" className="w-full h-full object-contain p-2" />
                                        ) : (
                                            <>
                                                <ImageIcon size={32} className="text-gray-600 mb-2" />
                                                <span className="text-xs text-gray-600 font-bold">Geen Logo</span>
                                            </>
                                        )}
                                    </div>
                                    
                                    <div className="flex-1 space-y-4">
                                        <label className="inline-flex items-center justify-center gap-2 bg-white text-black font-bold px-6 py-3 rounded-xl cursor-pointer hover:bg-gray-100 transition-all shadow-lg shadow-white/5">
                                            <Upload size={18} />
                                            {uploadingLogo ? 'Uploaden...' : 'Nieuw Logo Uploaden'}
                                            <input 
                                                type="file" 
                                                accept="image/*" 
                                                className="hidden" 
                                                onChange={handleLogoUpload}
                                                disabled={uploadingLogo}
                                            />
                                        </label>
                                        <p className="text-xs text-gray-500">Aanbevolen: Transparante PNG of SVG (Max 2MB)</p>
                                    </div>
                                </div>
                            </div>
                        ) : (
                            <div className="bg-white/5 border border-white/10 rounded-3xl p-8 opacity-50 relative">
                                <div className="absolute inset-0 flex items-center justify-center z-10">
                                    <div className="bg-black/80 backdrop-blur-md border border-white/10 px-4 py-2 rounded-full flex items-center gap-2 text-sm font-bold text-yellow-500 shadow-2xl">
                                        <Crown size={16} /> Alleen voor Bedrijfspoules
                                    </div>
                                </div>
                                <div className="flex items-center gap-3 mb-6">
                                    <ImageIcon size={20} className="text-gray-500" />
                                    <h3 className="text-xl font-bold text-gray-400">Branding & Logo</h3>
                                </div>
                                <div className="w-32 h-32 bg-black/20 border-2 border-dashed border-white/10 rounded-2xl"></div>
                            </div>
                        )}

                        {/* 🚀 UITNODIGINGEN SECTIE IN BEHEER */}
                        <div className="bg-white/5 border border-white/10 rounded-3xl p-8">
                            <div className="flex items-center gap-3 mb-6">
                                <div className="bg-orange-500/20 p-2 rounded-lg text-orange-500">
                                    <Users size={20} />
                                </div>
                                <h3 className="text-xl font-bold">Spelers Uitnodigen</h3>
                            </div>
                            
                            <p className="text-gray-400 text-sm mb-6">
                                Deel de unieke link of code met je collega's en vrienden zodat ze direct mee kunnen doen met deze poule.
                            </p>

                            <div className="flex flex-col md:flex-row gap-4">
                                <button 
                                    onClick={handleWhatsAppShare}
                                    className="flex-1 flex items-center justify-center gap-3 bg-[#25D366] hover:bg-[#20ba5a] text-white font-black px-6 py-4 rounded-2xl transition-all shadow-xl shadow-[#25D366]/20 active:scale-95 text-lg"
                                >
                                    <svg viewBox="0 0 24 24" width="24" height="24" fill="currentColor">
                                        <path d="M.057 24l1.687-6.163c-1.041-1.804-1.588-3.849-1.587-5.946.003-6.556 5.338-11.891 11.893-11.891 3.181.001 6.167 1.24 8.413 3.488 2.245 2.248 3.481 5.236 3.48 8.414-.003 6.557-5.338 11.892-11.893 11.892-1.99-.001-3.951-.5-5.688-1.448l-6.305 1.654zm6.597-3.807c1.676.995 3.276 1.591 5.392 1.592 5.448 0 9.886-4.434 9.889-9.885.002-5.462-4.415-9.89-9.881-9.892-5.452 0-9.887 4.434-9.889 9.884-.001 2.225.651 3.891 1.746 5.634l-.999 3.648 3.742-.981zm11.387-5.464c-.074-.124-.272-.198-.57-.347-.297-.149-1.758-.868-2.031-.967-.272-.099-.47-.149-.669.149-.198.297-.768.967-.941 1.165-.173.198-.347.223-.644.074-.297-.149-1.255-.462-2.39-1.475-.883-.788-1.48-1.761-1.653-2.059-.173-.297-.018-.458.13-.606.134-.133.297-.347.446-.521.151-.172.2-.296.3-.495.099-.198.05-.372-.025-.521-.075-.148-.669-1.611-.916-2.206-.242-.579-.487-.501-.669-.512-.173-.009-.371-.01-.57-.01-.198 0-.52.074-.792.372s-1.04 1.016-1.04 2.479 1.065 2.876 1.213 3.074c.149.198 2.095 3.2 5.076 4.487.709.306 1.263.489 1.694.626.712.226 1.36.194 1.872.118.571-.085 1.758-.719 2.006-1.413.248-.695.248-1.29.173-1.414z"/>
                                    </svg>
                                    WhatsApp Deelknop
                                </button>
                                
                                <Button 
                                    variant="outline" 
                                    onClick={handleShare}
                                    className={`flex-1 py-4 text-lg rounded-2xl border-2 border-dashed transition-all ${copied ? 'border-green-500 bg-green-500/10 text-green-400' : 'border-white/20 hover:border-orange-500'}`}
                                >
                                    {copied ? 'Link Gekopieerd! ✅' : 'Kopieer Link'}
                                </Button>
                            </div>
                        </div>

                    </div>
                )}
            </main>

            {/* === COMPETITOR VIEW MODAL === */}
            {selectedCompetitor && (
                <div className="fixed inset-0 z-[90] flex items-center justify-center p-4 backdrop-blur-md bg-black/80 animate-fadeIn">
                    <div className="bg-[#111] border border-white/10 w-full max-w-2xl h-[80vh] flex flex-col rounded-3xl overflow-hidden shadow-2xl shadow-black/50 relative">
                        
                        {/* Modal Header */}
                        <div className="p-6 border-b border-white/10 flex items-center justify-between bg-white/5">
                            <div className="flex items-center gap-4">
                                <div className="w-12 h-12 rounded-full bg-gradient-to-br from-orange-500 to-pink-600 flex items-center justify-center text-white font-black text-xl shadow-lg">
                                    {selectedCompetitor.full_name?.[0]?.toUpperCase()}
                                </div>
                                <div>
                                    <h3 className="font-black text-xl text-white flex items-center gap-2">
                                        Voorspellingen van {selectedCompetitor.full_name}
                                    </h3>
                                    <div className="text-sm text-orange-500 font-bold">
                                        Totaal Score: {selectedCompetitor.total_points} punten
                                    </div>
                                </div>
                            </div>
                            <button 
                                onClick={() => setSelectedCompetitor(null)}
                                className="p-2 rounded-full hover:bg-white/10 text-gray-400 hover:text-white transition-colors"
                            >
                                <X size={24} />
                            </button>
                        </div>

                        {/* Modal Content Scroll Area */}
                        <div className="flex-1 overflow-y-auto p-6 space-y-8 bg-black/40">
                            {compLoading ? (
                                <div className="flex flex-col items-center justify-center h-full text-gray-400 gap-4">
                                    <div className="animate-spin rounded-full h-10 w-10 border-t-2 border-b-2 border-orange-500"></div>
                                    <div>Laden...</div>
                                </div>
                            ) : (
                                <>
                                    {/* Bonus answers display */}
                                    <div className="bg-gradient-to-br from-amber-500/10 to-orange-600/10 border border-amber-500/20 rounded-2xl p-4">
                                        <div className="flex items-center gap-2 text-amber-500 mb-3">
                                            <Crown size={16} />
                                            <span className="text-xs font-black uppercase tracking-wider">Bonusvragen</span>
                                        </div>
                                        
                                        {!matches.some(m => new Date(m.kickoff_time) < new Date()) && selectedCompetitor.user_id !== user.id ? (
                                            <div className="text-xs text-amber-200/50 italic flex items-center gap-1 bg-black/30 px-3 py-2 rounded-lg border border-amber-500/10">
                                                🔒 De bonuskeuzes zijn geheim tot aan de aftrap van het WK.
                                            </div>
                                        ) : (
                                            <div className="grid grid-cols-1 md:grid-cols-2 gap-3">
                                                <div className="bg-black/30 p-3 rounded-xl border border-white/5">
                                                    <div className="text-[10px] text-gray-500 font-bold">KAMPIOEN</div>
                                                    <div className="text-white font-black text-sm mt-1">{competitorBonus?.winner_prediction || '—'}</div>
                                                </div>
                                                <div className="bg-black/30 p-3 rounded-xl border border-white/5">
                                                    <div className="text-[10px] text-gray-500 font-bold">TOPSCORER</div>
                                                    <div className="text-white font-black text-sm mt-1">{competitorBonus?.topscorer_prediction || '—'}</div>
                                                </div>
                                            </div>
                                        )}
                                    </div>

                                    <div className="space-y-6">
                                        {Object.entries(
                                            matches.reduce((groups, match) => {
                                                const dateStr = new Date(match.kickoff_time).toLocaleDateString('nl-NL', { weekday: 'long', day: 'numeric', month: 'long' });
                                                if (!groups[dateStr]) groups[dateStr] = [];
                                                groups[dateStr].push(match);
                                                return groups;
                                            }, {})
                                        ).map(([dateLabel, dayMatches]) => (
                                            <div key={dateLabel} className="space-y-3">
                                                <h4 className="text-xs font-bold text-gray-500 uppercase tracking-widest border-b border-white/5 pb-1">{dateLabel}</h4>
                                                <div className="space-y-2">
                                                    {dayMatches.map(m => {
                                                        const hasStarted = new Date(m.kickoff_time) < new Date();
                                                        const p = competitorPredictions[m.id];
                                                        
                                                        return (
                                                            <div key={m.id} className="bg-white/5 rounded-xl p-3 flex items-center justify-between border border-white/5">
                                                                <div className="flex items-center gap-3 flex-1">
                                                                    <span className="text-xs text-gray-500 w-10 font-mono">{new Date(m.kickoff_time).toLocaleTimeString('nl-NL', {hour:'2-digit', minute:'2-digit'})}</span>
                                                                    <div className="flex items-center gap-2 text-sm font-bold">
                                                                        <span className="text-gray-300">{t(`wk_poule.countries.${m.home_team?.name}`, m.home_team?.name)}</span>
                                                                        <span className="text-gray-600">-</span>
                                                                        <span className="text-gray-300">{t(`wk_poule.countries.${m.away_team?.name}`, m.away_team?.name)}</span>
                                                                    </div>
                                                                </div>

                                                                <div className="flex items-center gap-4">
                                                                    {!hasStarted && selectedCompetitor.user_id !== user.id ? (
                                                                        <div className="text-xs text-gray-500 italic flex items-center gap-1 bg-black/30 px-3 py-1 rounded-lg border border-white/5">
                                                                            🔒 Verborgen tot aftrap
                                                                        </div>
                                                                    ) : !p ? (
                                                                        <div className="text-xs text-gray-600 italic">Niet ingevuld</div>
                                                                    ) : (
                                                                        <div className="flex items-center gap-3">
                                                                            <div className={`bg-black/50 border text-orange-400 font-black px-3 py-1 rounded-lg text-sm flex gap-2 items-center ${p.isJoker ? 'border-orange-400 shadow-[0_0_10px_rgba(251,146,60,0.3)]' : 'border-orange-500/30'}`}>
                                                                                {p.isJoker && <Zap size={12} fill="currentColor" className="text-orange-400" />}
                                                                                <span>{p.home}</span>
                                                                                <span className="text-gray-600">:</span>
                                                                                <span>{p.away}</span>
                                                                            </div>
                                                                            {m.status === 'finished' && (
                                                                                <span className="text-xs font-black bg-orange-500 text-white px-2 py-0.5 rounded">
                                                                                    +{p.points || 0}
                                                                                </span>
                                                                            )}
                                                                        </div>
                                                                    )}
                                                                </div>
                                                            </div>
                                                        );
                                                    })}
                                                </div>
                                            </div>
                                        ))}
                                    </div>
                                </>
                            )}
                        </div>
                    </div>
                </div>
            )}
            {/* === FLOATING SAVE BAR === */}
            {hasUnsavedChanges && activeTab === 'predictions' && (
                <div className="fixed bottom-8 left-1/2 -translate-x-1/2 z-50 animate-slideUp">
                    <div className="bg-orange-500 text-white px-6 py-4 rounded-2xl shadow-2xl shadow-orange-500/40 flex items-center gap-6 border border-white/20 backdrop-blur-lg">
                        <div className="flex items-center gap-3">
                            <div className="w-2 h-2 bg-white rounded-full animate-pulse"></div>
                            <span className="font-bold text-sm whitespace-nowrap">Je hebt onopgeslagen wijzigingen!</span>
                        </div>
                        <Button 
                            variant="primary" 
                            size="sm"
                            onClick={savePredictions} 
                            disabled={saving} 
                            icon={<Save size={16} />} 
                            className="bg-white text-orange-600 hover:bg-gray-100 border-none font-black"
                        >
                            {saving ? 'Opslaan...' : 'Nu Opslaan'}
                        </Button>
                    </div>
                </div>
            )}
        </div>
    );
};
